import { WebStorageStateStore } from "oidc-client-ts";
import type { AuthProviderProps } from "react-oidc-context";
import { config } from "./config";

/**
 * Build settings for `react-oidc-context`'s AuthProvider.
 *
 * Must be called AFTER `loadRuntimeConfig()` populates `config.cognitoAuthority`
 * etc. — otherwise `authority` is captured empty and `oidc-client-ts` errors
 * with "No authority or metadataUrl configured on settings". Cognito's Hosted
 * UI domain is the authority; discovery + JWKS hang off it.
 */
export function buildOidcConfig(): AuthProviderProps {
  return {
    authority: config.cognitoAuthority,
    client_id: config.cognitoClientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: window.location.origin,
    response_type: "code",
    scope: "openid email profile",
    // Skip the IdP picker on Cognito's Hosted UI when we have a single SAML
    // IdP. Cognito honors `identity_provider` on the /authorize call to jump
    // straight to the SAML AuthnRequest.
    extraQueryParams: config.cognitoSamlIdpName
      ? { identity_provider: config.cognitoSamlIdpName }
      : undefined,
    loadUserInfo: false,
    automaticSilentRenew: true,
    // Persist across hard reloads so the user doesn't re-auth on every page load.
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // After the code exchange, drop the `?code=...` from the URL.
    onSigninCallback: () => {
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };
}

// Module-level handle to the current ID token, set by <AuthBridge/> below.
// Read by api.ts (which can't use React hooks).
let _currentIdToken: string | null = null;
let _signIn: (() => void) | null = null;
let _signOut: (() => void) | null = null;
let _userEmail: string | null = null;

export function setCurrentIdToken(token: string | null): void {
  _currentIdToken = token;
}

export function getCurrentIdToken(): string | null {
  return _currentIdToken;
}

export function setSignInTrigger(fn: (() => void) | null): void {
  _signIn = fn;
}

export function triggerSignIn(): void {
  _signIn?.();
}

export function setSignOutTrigger(fn: (() => void) | null): void {
  _signOut = fn;
}

export function triggerSignOut(): void {
  _signOut?.();
}

export function setUserEmail(email: string | null): void {
  _userEmail = email;
}

export function getUserEmail(): string | null {
  return _userEmail;
}

export function isAuthSessionActive(): boolean {
  return _currentIdToken !== null;
}
